import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
// routes
import { Box } from '@mui/material';
import { useSearchParams, useRouter } from 'src/routes/hooks';
// config
import { PATH_AFTER_LOGIN, PATH_AFTER_LOGIN_DEVICE_ONLY, PATH_AFTER_LOGIN_IFTA_ONLY, PATH_AFTER_LOGIN_ELD_ONLY, VERSION } from 'src/config-global';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import Iconify from 'src/components/iconify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { BackendValidationError } from 'src/utils/backend-validation-error';
import { INTERNAL_SERVER_ERROR, UNKNOWN_ERROR } from 'src/utils/axios';

// ----------------------------------------------------------------------

export default function LoginView() {
  const { login } = useAuthContext();

  const router = useRouter();

  const [errorMsgs, setErrorMsgs] = useState<BackendValidationError[]>([]);

  const searchParams = useSearchParams();

  const returnTo = searchParams.get('returnTo');

  const password = useBoolean();

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const session = await login?.(data.email, data.password);

      // eslint-disable-next-line no-nested-ternary
      router.push(returnTo || (session?.organization?.eldOnly ? PATH_AFTER_LOGIN_ELD_ONLY : session?.organization?.deviceOnly ? PATH_AFTER_LOGIN_DEVICE_ONLY : (session?.organization?.iftaOnly ? PATH_AFTER_LOGIN_IFTA_ONLY : PATH_AFTER_LOGIN)));
    } catch (error) {
      setErrorMsgs(error?.response?.data || [{
        message:
          error?.response?.status === 500 ? INTERNAL_SERVER_ERROR : UNKNOWN_ERROR
      }]);
    }
  });

  const renderHead = (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <Typography variant="h4" textAlign="start" sx={{fontWeight: 500}}>Sign in</Typography>

      {/* <Stack direction="row" spacing={0.5} justifyContent="center">
        <Typography variant="body2">New Organization?</Typography>

        <Link component={RouterLink} href={paths.auth.register} variant="subtitle2">
          Create an account
        </Link>
      </Stack> */}
    </Stack>
  );

  const renderForm = (
    <Stack spacing={2.5}>
      {errorMsgs.length > 0 && errorMsgs.map((error, index) => <Alert key={index} severity="error">{error.message}</Alert>)}

      <RHFTextField name="email" sx={{background: "white"}} label="Email Address" />

      <RHFTextField
        name="password"
        label="Password"
        type={password.value ? 'text' : 'password'}
        sx={{background: "white"}}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* <Link href="/auth/forgot-password" variant="body2" color="inherit" underline="always" sx={{ alignSelf: 'flex-end' }}>
        Forgot password?
      </Link> */}

      <LoadingButton
        fullWidth
        color="secondary"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        sx={{}}
      >
        Login
      </LoadingButton>
    </Stack>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      {renderHead}

      {renderForm}

      <Box display="none">version: {VERSION}</Box>
    </FormProvider>
  );
}
